/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @fileoverview Client module `useQRScanFlow` for the Caintra Connect frontend.
 * @module hooks/useQRScanFlow
 */
import { useCallback } from "react";
import { refreshToken, signOut } from "../utils/common.js";
import { buildQrScanEventConfig } from "./qrScan/buildQrScanEventConfig.js";

const SCAN_COOLDOWN_MS = 3000;

/**
 * Runs a guarded QR scan with shared cooldown and loading state handling.
 */
const runGuardedScan = async ({
  decodedText,
  isProcessingRef,
  lastScannedRef,
  cooldownTimeoutRef,
  setIsLoading,
  setConfirmationStatus,
  parsePayload,
  processScan,
  onParseFailure,
}) => {
  if (isProcessingRef.current || lastScannedRef.current === decodedText) {
    return;
  }

  let payload;
  try {
    payload = parsePayload(decodedText);
  } catch {
    setConfirmationStatus("invalidQRCode");
    isProcessingRef.current = false;
    onParseFailure?.();
    return;
  }

  if (!payload.valid) {
    setConfirmationStatus(payload.errorStatus);
    return;
  }

  isProcessingRef.current = true;
  lastScannedRef.current = decodedText;
  setIsLoading(true);
  setConfirmationStatus(null);

  try {
    await processScan(payload);
  } finally {
    setIsLoading(false);
    if (cooldownTimeoutRef.current) {
      clearTimeout(cooldownTimeoutRef.current);
    }
    cooldownTimeoutRef.current = setTimeout(() => {
      lastScannedRef.current = null;
      isProcessingRef.current = false;
    }, SCAN_COOLDOWN_MS);
  }
};

/**
 * Provides QR scan success handling per event type.
 */
export const useQRScanFlow = ({
  selectedEvent,
  navigate,
  onQRCodeScanned,
  isProcessingRef,
  lastScannedRef,
  cooldownTimeoutRef,
  setIsLoading,
  setConfirmationStatus,
}) => {
  const confirmRegistrationAttendance = useCallback(
    async (userId, eventId) => {
      const token = localStorage.getItem("token");
      const options = {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          idUsuario: userId,
          idEvento: eventId,
          estatus: true,
        }),
      };

      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}attendee/attendance/confirm`,
        options,
      );
      const data = await response.json();

      if (data?.message === "The incoming token has expired") {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        return confirmRegistrationAttendance(userId, eventId);
      }

      return data;
    },
    [navigate],
  );

  const onScanSuccess = useCallback(
    async (decodedText) => {
      const eventConfig = buildQrScanEventConfig({
        eventType: selectedEvent.tipoEvento,
        selectedEventId: selectedEvent._id,
        navigate,
        confirmRegistrationAttendance,
        setConfirmationStatus,
        onQRCodeScanned,
      });

      if (!eventConfig) {
        return;
      }

      await runGuardedScan({
        decodedText,
        isProcessingRef,
        lastScannedRef,
        cooldownTimeoutRef,
        setIsLoading,
        setConfirmationStatus,
        parsePayload: eventConfig.parsePayload,
        processScan: eventConfig.processScan,
        onParseFailure: eventConfig.onParseFailure,
      });
    },
    [
      confirmRegistrationAttendance,
      cooldownTimeoutRef,
      isProcessingRef,
      lastScannedRef,
      navigate,
      onQRCodeScanned,
      selectedEvent._id,
      selectedEvent.tipoEvento,
      setConfirmationStatus,
      setIsLoading,
    ],
  );

  return { onScanSuccess };
};
